import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBatches, fetchLeadSources, type LeadFilter } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import LeadsBrowser, { type LeadsView } from './LeadsBrowser';
import AssignPanel from './AssignPanel';
import { fmtDay, fmtInt } from './ui';

/** The leads an assignment draws from, captured when the operator moves on. */
export interface AssignmentSeed {
  filter: LeadFilter;
  sortKey: string;
  desc: boolean;
  selectedIds: number[];
  total: number;
  /** total is a floor: the filters match more leads than were counted. */
  totalIsLowerBound: boolean;
  /** Every matching lead, not just the checked ones. */
  allMatching: boolean;
}

export const seedFrom = (v: LeadsView): AssignmentSeed => ({
  filter: v.filter, sortKey: v.sortKey, desc: v.desc, total: v.total, totalIsLowerBound: v.totalIsLowerBound, allMatching: v.allMatching,
  selectedIds: v.allMatching ? [] : v.selectedIds,
});

/** How many leads a seed or view stands for, and how to say it ("10,000+" when the count is a floor). */
const chosenCount = (c: { selectedIds: number[]; total: number; totalIsLowerBound: boolean; allMatching: boolean }) =>
  c.allMatching || !c.selectedIds.length
    ? { n: c.total, text: `${fmtInt(c.total)}${c.totalIsLowerBound ? '+' : ''}`, how: 'matching' }
    : { n: c.selectedIds.length, text: fmtInt(c.selectedIds.length), how: 'checked' };

const ASSIGNMENT_LABEL: Record<string, string> = { unassigned: 'Not assigned', never_assigned: 'Never assigned', assigned: 'Assigned' };
const REPEAT_LABEL: Record<string, string> = { repeats: 'Seen before', first_seen: 'First time' };
const ELIGIBILITY_LABEL: Record<string, string> = { eligible: 'Assignable', not_eligible: 'Not assignable' };

/**
 * A new assignment that is not tied to one import: choose leads from any
 * imports, then give them to a rep. A seed from the Leads tab skips straight
 * to the second step; "Change leads" goes back with the grid as it was left.
 */
export default function NewAssignment({ seed, onOpenLead, onDone }: {
  seed: AssignmentSeed | null;
  onOpenLead: (id: number) => void;
  onDone: () => void;
}) {
  const [chosen, setChosen] = useState<AssignmentSeed | null>(seed);
  const [step, setStep] = useState<1 | 2>(seed ? 2 : 1);

  return (
    <div className="li-newassign">
      <nav className="li-stepper" aria-label="Assignment steps">
        <ol>
          {(['Choose leads', 'Give to a rep'] as const).map((label, i) => {
            const n = (i + 1) as 1 | 2;
            const state = n === step ? 'current' : n < step ? 'done' : 'todo';
            return (
              <li key={label} className={`li-stepper__item is-${state}`}>
                <button type="button" disabled={n === 2 && !chosen} onClick={() => setStep(n)} aria-current={state === 'current' ? 'step' : undefined}>
                  <span className="li-stepper__num" aria-hidden>{state === 'done' ? '✓' : n}</span>
                  <span className="li-stepper__label">{label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Kept mounted so the filters, sort and checks survive a trip to step 2 and back. */}
      <div hidden={step !== 1}>
        <LeadsBrowser
          initial={{ assignment: 'unassigned', eligibility: 'eligible', ...seed?.filter }}
          initialSort={seed ? { sortKey: seed.sortKey, desc: seed.desc } : undefined}
          createMerchants={false}
          onOpenLead={onOpenLead}
          height="calc(100vh - 490px)"
          renderFooter={(v) => { const c = chosenCount(v); return (
            <div className="li-wizfoot">
              <span>
                <b>{c.text}</b> {v.allMatching ? 'leads, all matching' : `${c.how} ${c.n === 1 ? 'lead' : 'leads'}`}
                <span className="li-muted">
                  {v.allMatching ? '. Every lead these filters match can be given out.'
                    : v.selectedIds.length ? '. Only the checked rows are given out.'
                      : '. Check rows to pick them one by one, or continue to give out all of them or the first or a random N.'}
                </span>
              </span>
              <button type="button" className="li-btn li-btn--primary" disabled={v.total === 0}
                onClick={() => { setChosen(seedFrom(v)); setStep(2); }}>
                Continue
              </button>
            </div>
          ); }}
        />
      </div>

      {step === 2 && chosen && (
        <div className="li-newassign__two">
          <Recap chosen={chosen} onChange={() => setStep(1)} />
          <div className="li-newassign__panel">
            <AssignPanel
              selectedIds={chosen.selectedIds}
              allMatching={chosen.allMatching}
              viewFilter={chosen.filter}
              sort={chosen.sortKey}
              desc={chosen.desc}
              matching={chosen.totalIsLowerBound ? undefined : chosen.total}
              onSaved={onDone}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** What step 1 chose, in words, so step 2 can be checked at a glance. */
function Recap({ chosen, onChange }: { chosen: AssignmentSeed; onChange: () => void }) {
  const f = chosen.filter;
  const batches = useQuery({
    queryKey: leadImportKeys.batches({ status: 'Committed' }), queryFn: () => fetchBatches({ status: 'Committed' }),
    enabled: !!f.batches?.length, ...queryPolicies.pagedData,
  });
  const sources = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, enabled: !!f.sources?.length, ...queryPolicies.configuration });
  const names = <T,>(ids: number[] | undefined, rows: T[] | undefined, id: (r: T) => number, name: (r: T) => string) =>
    (ids ?? []).map((x) => { const r = rows?.find((y) => id(y) === x); return r ? name(r) : `#${x}`; }).join(', ');
  const dates = f.date_from && f.date_to ? `${fmtDay(f.date_from)} to ${fmtDay(f.date_to)}`
    : f.date_from ? `From ${fmtDay(f.date_from)}` : f.date_to ? `Up to ${fmtDay(f.date_to)}` : null;
  const narrowing = [
    f.assignment && ASSIGNMENT_LABEL[f.assignment],
    f.repeat && REPEAT_LABEL[f.repeat],
    f.eligibility && ELIGIBILITY_LABEL[f.eligibility],
    f.search && `Matching "${f.search}"`,
  ].filter(Boolean).join(', ');
  const { n, text, how } = chosenCount(chosen);

  return (
    <section className="li-recap" aria-label="Chosen leads">
      <div className="li-recap__count">
        <b>{text}</b>
        <span>{how} {n === 1 ? 'lead' : 'leads'}</span>
      </div>
      <dl className="li-dl li-dl--tight">
        <dt>Imports</dt>
        <dd>{f.batches?.length ? names(f.batches, batches.data, (b) => b.id, (b) => b.file_name ?? `Import ${b.id}`) : 'All imports'}</dd>
        <dt>Sources</dt>
        <dd>{f.sources?.length
          ? `${f.sources_exclude ? 'All except ' : ''}${names(f.sources, sources.data, (x) => x.id, (x) => x.source)}`
          : 'All sources'}</dd>
        <dt>Batch dates</dt><dd>{dates ?? 'Any'}</dd>
        <dt>Leads</dt><dd>{narrowing || 'Any'}</dd>
      </dl>
      <button type="button" className="li-btn li-btn--secondary" onClick={onChange}>Change leads</button>
    </section>
  );
}
